import { useState } from "react";
import { HelpCircle, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";

const QNA_DATA = [
  {
    question:
      "1. Apa yang berbeda pada sistem remunerasi Undiksha 2026 dibandingkan sistem sebelumnya?",
    answer:
      "Sistem remunerasi Undiksha yang baru mengadaptasi Permendiktisaintek Nomor 23 Tahun 2025 tentang Pemberian Tunjangan Kinerja (Tukin) bagi Pegawai di Lingkungan Kementerian Pendidikan Tinggi, Sains, dan Teknologi.",
  },
  {
    question:
      "2. Bagaimana besaran remunerasi maksimal setiap dosen ditentukan?",
    answer: (
      <div className="space-y-3">
        <p>
          Besaran remunerasi maksimal (100%) setiap dosen ditentukan berdasarkan
          jabatan fungsional, kelas jabatan, dan nilai tunjangan kinerja.
          Penetapan tersebut mengacu pada Keputusan Sekretaris Jenderal
          Kementerian Pendidikan Tinggi, Sains, dan Teknologi Nomor
          21/A/KEP/2025 tentang Petunjuk Teknis Tata Cara Penghitungan dan
          Pembayaran Tunjangan Kinerja Pegawai dengan Jabatan Fungsional Dosen,
          yang kemudian diadaptasi dalam sistem remunerasi Universitas
          Pendidikan Ganesha.
        </p>
        <p>
          Besaran remunerasi maksimal yang menjadi dasar perhitungan di Undiksha
          adalah sebagai berikut.
        </p>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-50 text-slate-900 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 border-r">Jabatan Fungsional</th>
                <th className="px-4 py-2 border-r text-center">
                  Kelas Jabatan
                </th>
                <th className="px-4 py-2 text-right">
                  Besaran Remunerasi Maksimal (100%)
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-2 border-r">CPNS</td>
                <td className="px-4 py-2 border-r text-center">7</td>
                <td className="px-4 py-2 text-right">Rp3.915.950</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r">
                  Dosen ASN belum memiliki Jabatan Akademik
                </td>
                <td className="px-4 py-2 border-r text-center">8</td>
                <td className="px-4 py-2 text-right">Rp4.595.150</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r">Asisten Ahli</td>
                <td className="px-4 py-2 border-r text-center">9</td>
                <td className="px-4 py-2 text-right">Rp5.079.200</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r">Lektor</td>
                <td className="px-4 py-2 border-r text-center">11</td>
                <td className="px-4 py-2 text-right">Rp8.757.600</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r">Lektor Kepala</td>
                <td className="px-4 py-2 border-r text-center">13</td>
                <td className="px-4 py-2 text-right">Rp10.936.000</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r">Profesor</td>
                <td className="px-4 py-2 border-r text-center">15</td>
                <td className="px-4 py-2 text-right">Rp19.280.000</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs bg-amber-50 text-amber-800 p-2 rounded-md border border-amber-200">
          <strong>Catatan:</strong> Besaran tersebut merupakan nilai remunerasi
          maksimal (100%) sesuai kelas jabatan. Nilai remunerasi yang diterima
          setiap dosen selanjutnya ditentukan berdasarkan capaian Kinerja Dasar
          (60%) dan Kinerja Prestasi/IKU (40%), serta bagi dosen yang menerima
          Sertifikasi Dosen akan diperhitungkan sesuai ketentuan yang berlaku.
        </p>
      </div>
    ),
  },
  {
    question:
      "3. Mengapa nilai Sertifikasi Dosen (Serdos) mengurangi remunerasi?",
    answer:
      "Bagi dosen yang menerima Sertifikasi Dosen, besaran remunerasi maksimal terlebih dahulu dikurangi dengan nilai Serdos yang diterima. Ketentuan ini mengikuti prinsip pemberian Tukin agar tidak terjadi pembayaran ganda atas komponen penghargaan terhadap kinerja.",
  },
  {
    question: "4. Dari mana dosen memperoleh remunerasi maksimalnya?",
    answer: (
      <div>
        <p className="mb-2">
          Remunerasi diperoleh dari dua komponen kinerja, yaitu:
        </p>
        <ul className="list-disc pl-5 space-y-1 mb-2">
          <li>60% Kinerja Dasar, dan</li>
          <li>40% Kinerja Prestasi (IKU).</li>
        </ul>
        <p>
          Semakin tinggi capaian kedua komponen tersebut, semakin besar
          remunerasi yang diperoleh hingga mencapai nilai maksimal.
        </p>
      </div>
    ),
  },
  {
    question: "5. Apa yang dimaksud dengan Kinerja Dasar?",
    answer:
      "Kinerja Dasar merupakan capaian yang dihitung berdasarkan Beban Kerja Dosen (BKD), meliputi pendidikan, penelitian, pengabdian kepada masyarakat, dan unsur penunjang yang dikonversi menjadi poin remunerasi.",
  },
  {
    question: "6. Apa yang dimaksud dengan Kinerja Prestasi (IKU)?",
    answer: (
      <div className="space-y-3">
        <p>
          Kinerja Prestasi merupakan capaian Indikator Kinerja Utama (IKU) yang
          mendukung pencapaian kontrak kinerja Rektor. Komponen ini memiliki
          bobot 40% dari total remunerasi dan dihitung berdasarkan capaian dosen
          pada indikator-indikator strategis universitas.
        </p>
        <p>
          Indikator IKU yang digunakan dalam sistem remunerasi Undiksha
          meliputi:
        </p>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="min-w-full text-sm text-left text-slate-700">
            <thead className="bg-slate-50 text-slate-900 border-b border-slate-200">
              <tr>
                <th className="px-4 py-2 border-r text-center w-16">No.</th>
                <th className="px-4 py-2">Indikator IKU</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 1</td>
                <td className="px-4 py-2">Angka Efisiensi Edukasi</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 2</td>
                <td className="px-4 py-2">
                  Lulusan Bekerja/Wirausaha/Studi Lanjut
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 3</td>
                <td className="px-4 py-2">
                  Mahasiswa Berkegiatan/Meraih Prestasi
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 4</td>
                <td className="px-4 py-2">Rekognisi Internasional</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 5</td>
                <td className="px-4 py-2">Kerja Sama dan Hilirisasi</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 6</td>
                <td className="px-4 py-2">Publikasi Bereputasi</td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 7</td>
                <td className="px-4 py-2">
                  Keterlibatan dalam Pencapaian SDGs
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 8</td>
                <td className="px-4 py-2">
                  Kebijakan dan Implementasi Tri Dharma Berbasis THK
                </td>
              </tr>
              <tr>
                <td className="px-4 py-2 border-r text-center">IKU 9</td>
                <td className="px-4 py-2">Pendapatan Non-UKT</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Capaian IKU dikonversi menjadi poin remunerasi dengan batas maksimal
          40% dari total poin sesuai jabatan fungsional dosen dalam satu tahun.
        </p>
      </div>
    ),
  },
  {
    question: "7. Bagaimana capaian kinerja dihitung menjadi remunerasi?",
    answer:
      "Seluruh capaian Kinerja Dasar dan Kinerja Prestasi dikonversi menjadi total poin, kemudian dikonversi lagi menjadi nilai rupiah sesuai nilai jabatan masing-masing dosen.",
  },
  {
    question: "8. Bagaimana mekanisme pembayaran remunerasi 30%?",
    answer:
      "Pembayaran 30% dihitung dari 30% nilai remunerasi maksimal setelah dikurangi nilai Sertifikasi Dosen (bagi dosen yang menerima Serdos).",
  },
  {
    question: "9. Bagaimana mekanisme pembayaran remunerasi 70%?",
    answer: (
      <div className="space-y-2">
        <p>
          Pembayaran 70% dihitung berdasarkan capaian kinerja aktual dosen.
          Besaran yang diterima merupakan nilai remunerasi sesuai capaian
          kinerja, dikurangi nilai Sertifikasi Dosen (Serdos) dan remunerasi 30%
          yang telah dibayarkan sebelumnya.
        </p>
        <p>
          Setiap dosen memiliki besaran serdos yang diterima berbeda-beda,
          sehingga besaran remun 30 % maupun 70% tidak akan sama setiap dosen
          walaupun memiliki kinerja yang sama. Namun, jika dosen sama-sama
          memiliki kinerja maksimal pada kinerja dasar dan prestasi kemudian
          ditambah besaran serdos yang diterima, maka akan memperoleh besaran
          sesuai dengan nilai maksimal remunerasi yang setara dengan Tukin.
        </p>
      </div>
    ),
  },
  {
    question: "10. Apakah setiap dosen dapat memperoleh remunerasi 100%?",
    answer:
      "Ya. Remunerasi 100% dapat diperoleh apabila dosen memenuhi capaian maksimal pada Kinerja Dasar (60%) dan Kinerja Prestasi (40%), sesuai target dan ketentuan yang berlaku.",
  },
];

export function QnaRemunerasiDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [openItems, setOpenItems] = useState<number[]>([]);

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  return (
    <>
      <div className="fixed bottom-22 right-6 z-50 flex items-center justify-center w-14 h-14 group">
        <span className="absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75 animate-ping"></span>
        <Button
          onClick={() => setIsOpen(true)}
          className="relative rounded-full w-14 h-14 shadow-lg flex items-center justify-center p-0 bg-blue-600 hover:bg-blue-700 text-white border-2 border-white transition-transform group-hover:scale-110"
          title="Q&A Remunerasi"
        >
          <span className="font-bold text-[13px] tracking-wide">QNA</span>
        </Button>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0 overflow-hidden bg-slate-50">
          <DialogHeader className="px-6 py-4 border-b border-slate-200 bg-white shrink-0">
            <DialogTitle className="flex items-center gap-2 text-xl font-bold text-slate-800">
              <HelpCircle className="w-6 h-6 text-blue-600" />Q & A Remunerasi
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6 space-y-4 custom-scrollbar">
            {QNA_DATA.map((item, index) => (
              <Collapsible
                key={index}
                open={openItems.includes(index)}
                onOpenChange={() => toggleItem(index)}
                className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden"
              >
                <CollapsibleTrigger className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-50 transition-colors">
                  <span className="font-semibold text-slate-800 leading-snug pr-4">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-slate-400 transition-transform ${openItems.includes(index) ? "rotate-180" : ""}`}
                  />
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 border-t border-slate-100 bg-slate-50/50">
                    <div className="text-slate-700 text-sm leading-relaxed mt-4">
                      <span className="font-semibold block mb-2 text-blue-800">
                        Jawaban:
                      </span>
                      {item.answer}
                    </div>
                  </div>
                </CollapsibleContent>
              </Collapsible>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
